// LevelUpNotifier.js — Toast FESTIF « 🎉 Niveau X atteint ! » quand le membre monte de niveau.
// Monté app-wide (à côté de FriendOnlineNotifier). 100% client : lit la progression AUTORITATIVE
// Nakama (auth.getXp → { level, xp, unlocks }) par polling léger + au focus de l'onglet, compare au
// dernier niveau vu (persisté par utilisateur), et notifie SEULEMENT à la HAUSSE. Le premier chargement
// sert de référence (aucun toast). Respecte la préférence notif.levelUp.
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';
import { loadPrefs, getPrefs } from '../services/preferences';

const POLL_MS = 25000;   // vérification périodique (l'XP est gagnée après les parties)
const KEY = (uid) => `vg_lastlevel_${uid}`;

const pop = keyframes`0%{transform:translateY(14px) scale(.96);opacity:0}60%{transform:translateY(0) scale(1.03)}100%{transform:translateY(0) scale(1);opacity:1}`;
const shine = keyframes`from{background-position:-160px}to{background-position:220px}`;
const Wrap = styled.div`position:fixed;left:1rem;bottom:1rem;z-index:4100;display:flex;flex-direction:column;gap:.5rem;`;
const Toast = styled.div`
  position:relative;overflow:hidden;display:flex;align-items:center;gap:.7rem;
  background:linear-gradient(135deg,#3a2c07,#5c4708);
  border:1px solid #d4a017;border-radius:${theme.radii.md};padding:.7rem 1rem;
  box-shadow:0 6px 22px rgba(0,0,0,.45),0 0 14px rgba(212,160,23,.35);
  color:#fff3d6;font-size:.9rem;animation:${pop} .35s ease;max-width:320px;
  .star{font-size:1.3rem;filter:drop-shadow(0 0 6px #ffcf47);flex-shrink:0;}
  .lvl{font-weight:800;color:#ffd35a;}
  .sub{display:block;font-size:.75rem;color:#e8d7a6;margin-top:.1rem;}
  &::after{content:'';position:absolute;top:0;left:0;width:80px;height:100%;
    background:linear-gradient(120deg,transparent,rgba(255,255,255,.35),transparent);
    animation:${shine} 1.6s ease .2s 1;}
`;

// Dédup au niveau MODULE (survit aux remontages / navigations) : un seul poller par session.
let polling = false;
export function _resetLevelUpNotifier() { polling = false; }

export default function LevelUpNotifier() {
  const { auth, isLoggedIn } = useAuth();
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    if (!isLoggedIn || !auth.enabled) { _resetLevelUpNotifier(); return undefined; }
    let alive = true;
    loadPrefs();

    const uid = (auth.session && auth.session.user_id) || null;
    if (!uid) return undefined;

    const readStored = () => { try { return parseInt(localStorage.getItem(KEY(uid)) || '', 10) || null; } catch { return null; } };
    const writeStored = (lvl) => { try { localStorage.setItem(KEY(uid), String(lvl)); } catch { /* */ } };

    const showToast = (level, unlocks) => {
      const nUnlocks = Array.isArray(unlocks) ? unlocks.length : 0;
      const tid = `${level}:${Date.now()}`;
      setToasts((prev) => [...prev.slice(-2), { id: tid, level, nUnlocks }]);
      setTimeout(() => { if (alive) setToasts((prev) => prev.filter((t) => t.id !== tid)); }, 7000);
    };

    const check = async () => {
      const p = await auth.getXp();
      if (!alive || !p || !p.ok || typeof p.level !== 'number') return;
      const prev = readStored();
      if (prev == null) { writeStored(p.level); return; }   // 1er passage = référence, pas de toast
      if (p.level > prev) {
        writeStored(p.level);
        if (getPrefs().notif.levelUp !== false) showToast(p.level, p.unlocks);
      } else if (p.level < prev) {
        writeStored(p.level);   // resync défensif (reset serveur éventuel) sans notifier
      }
    };

    check();   // vérif immédiate au montage
    let timer = null;
    if (!polling) { polling = true; timer = setInterval(check, POLL_MS); }
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);

    return () => {
      alive = false;
      window.removeEventListener('focus', onFocus);
      if (timer) { clearInterval(timer); polling = false; }
    };
  }, [auth, isLoggedIn]);

  if (!toasts.length) return null;
  return (
    <Wrap>
      {toasts.map((t) => (
        <Toast key={t.id}>
          <span className="star">🎉</span>
          <span>
            Bravo ! Tu passes <span className="lvl">Niveau {t.level}</span>
            {t.nUnlocks > 0 && <span className="sub">🎁 {t.nUnlocks} récompense{t.nUnlocks > 1 ? 's' : ''} débloquée{t.nUnlocks > 1 ? 's' : ''}</span>}
          </span>
        </Toast>
      ))}
    </Wrap>
  );
}
